import type { Backtest } from "../types/backtests";
import type {
  PolicyPickOverlapAction,
  PolicyPickOverlapPick,
} from "../types/policyPickOverlap";
import type { TradePolicyPolicyNode, TradePolicyPolicyPayload } from "../types/tradePolicy";
import {
  bucketTargetFromSummaryRow,
  mapSliceContextForResolve,
  type CohortTradesResolveTarget,
} from "./cohortTradesResolve";
import { isExitPlacementPick, sliceIdFromExitPlacementPick } from "./exitPlacementPicks";
import type { ReportPick } from "./reportPickListStorage";
import {
  isPolicyNode,
  pickImportPolicyNodes,
  type PickImportCatalogContext,
} from "./reportPickSlicePolicy";

export const MAX_POLICY_PICK_OVERLAP = 8;

export type OverlapPickSourceKind = "report_pick" | "saved_backtest" | "combined_bundle";

export type OverlapSelectionItem = {
  key: string;
  label: string;
  sourceKind: OverlapPickSourceKind;
  reportPickId?: string;
  backtestId?: string;
  /** Combined Kanban card id (when sourceKind is combined_bundle). */
  bundleId?: string;
  /**
   * Inline policy for a combined card that is not yet a saved backtest.
   * Built from the bundle's children at open time.
   */
  policyTree?: TradePolicyPolicyPayload | Record<string, unknown>;
  warning?: string;
};

export type MapReportPickResult =
  | { ok: true; pick: PolicyPickOverlapPick }
  | { ok: false; reason: string };

function policyNodesFromPick(pick: ReportPick): unknown[] | undefined {
  if (Array.isArray(pick.policyNodes) && pick.policyNodes.length > 0) {
    return pick.policyNodes;
  }
  return undefined;
}

function buildPolicyTreeFromNodes(
  nodes: TradePolicyPolicyNode[],
  action: PolicyPickOverlapAction
): Record<string, unknown> {
  const root: TradePolicyPolicyNode =
    nodes.length === 1 ? nodes[0] : { kind: "group", op: "and", children: nodes };
  return {
    schema_version: "policy_tree.v1",
    action,
    root,
  };
}

/** Pick narrows the report cohort beyond root / all (e.g. long-only slice). */
export function pickHasNarrowingSliceContext(pick: ReportPick): boolean {
  const ctx = pick.sliceContext;
  return Boolean(ctx && ctx.kind !== "all" && ctx.sliceId !== "all");
}

/**
 * Overlap target+slice_context is only equivalent to a sim when the policy is a bare bucket/segment
 * with no active slice narrowing. Slice-scoped picks must use inline policy_tree (or backtest_id).
 */
export function pickRequiresPolicyTreeForOverlap(
  pick: ReportPick,
  catalogCtx: PickImportCatalogContext
): boolean {
  if (pickHasNarrowingSliceContext(pick)) return true;

  const nodes = pickImportPolicyNodes(pick, catalogCtx);
  if (nodes.length === 0) return false;
  if (nodes.length > 1) return true;

  const only = nodes[0];
  if (only.kind === "group" && only.children.length > 1) return true;
  return false;
}

function buildTargetFromReportPick(
  pick: ReportPick
): CohortTradesResolveTarget | null {
  const policy = policyNodesFromPick(pick);
  const meta = pick.meta ?? {};

  if (pick.kind === "bucket") {
    const bucket = String(meta.bucket ?? pick.label ?? "").trim();
    if (!bucket) return null;
    return {
      type: "bucket",
      bucket,
      ...(policy ? { policy_import_nodes: policy } : {}),
    };
  }

  if (pick.kind === "situation") {
    const situation = String(meta.situation ?? pick.label ?? "").trim();
    if (!situation) return null;
    const clauses = Array.isArray(meta.clauses)
      ? (meta.clauses as Array<{ key: string; value: string }>)
      : undefined;
    return {
      type: "situation",
      situation,
      ...(clauses?.length ? { clauses } : {}),
      ...(policy ? { policy_import_nodes: policy } : {}),
    };
  }

  if (pick.kind === "segment") {
    const summaryTable = String(meta.summary_table ?? "").trim();
    const rowKey = String(meta.row_key ?? "").trim();
    let target: CohortTradesResolveTarget | null = null;
    if (summaryTable && rowKey) {
      target = bucketTargetFromSummaryRow(summaryTable, rowKey);
    }
    if (!target) {
      const segmentKey = String(meta.segment_key ?? rowKey ?? pick.sliceId ?? "").trim();
      if (segmentKey) {
        target = { type: "segment", key: segmentKey };
      }
    }
    if (!target) return null;
    if (policy) {
      target = { ...target, policy_import_nodes: policy };
    }
    return target;
  }

  if (pick.kind === "slice") {
    const sliceId = String(pick.sliceId ?? meta.slice_id ?? "").trim();
    if (!sliceId) return null;
    return {
      type: "slice",
      slice_id: sliceId,
      ...(policy ? { policy_import_nodes: policy } : {}),
    };
  }

  return null;
}

export function mapReportPickToOverlapPick(
  pick: ReportPick,
  action: PolicyPickOverlapAction,
  catalogCtx: PickImportCatalogContext,
  opts?: { backtestId?: string }
): MapReportPickResult {
  if (opts?.backtestId?.trim()) {
    return {
      ok: true,
      pick: {
        pick_id: pick.id,
        label: pick.label,
        backtest_id: opts.backtestId.trim(),
      },
    };
  }

  if (isExitPlacementPick(pick)) {
    const sliceId = sliceIdFromExitPlacementPick(pick);
    if (sliceId) {
      return {
        ok: true,
        pick: {
          pick_id: pick.id,
          label: pick.label,
          source: "management_explorer",
          action: "veto",
          target: { type: "slice", slice_id: sliceId },
        },
      };
    }

    return {
      ok: false,
      reason: "Exit placement pick is missing a slice id — re-add from the Market tab.",
    };
  }

  if (pickRequiresPolicyTreeForOverlap(pick, catalogCtx)) {
    const nodes = pickImportPolicyNodes(pick, catalogCtx);
    if (nodes.length === 0) {
      return {
        ok: false,
        reason:
          "Pick has slice context but could not build a matching policy tree — check catalog fields.",
      };
    }
    return {
      ok: true,
      pick: {
        pick_id: pick.id,
        label: pick.label,
        policy_tree: buildPolicyTreeFromNodes(nodes, action),
      },
    };
  }

  const sliceContext = mapSliceContextForResolve(pick.sliceContext);
  const target = buildTargetFromReportPick(pick);

  if (target) {
    const source =
      pick.kind === "slice" && pick.meta?.explorer_slice_id
        ? ("management_explorer" as const)
        : ("condition_analysis" as const);
    return {
      ok: true,
      pick: {
        pick_id: pick.id,
        label: pick.label,
        action,
        source,
        target,
        ...(sliceContext ? { slice_context: sliceContext } : {}),
      },
    };
  }

  const nodes = pickImportPolicyNodes(pick, catalogCtx);
  if (nodes.length === 0) {
    return { ok: false, reason: "Pick is not convertible to a policy rule or cohort target." };
  }

  return {
    ok: true,
    pick: {
      pick_id: pick.id,
      label: pick.label,
      policy_tree: buildPolicyTreeFromNodes(nodes, action),
    },
  };
}

export function mapBacktestToOverlapPick(backtest: Backtest): PolicyPickOverlapPick {
  return {
    pick_id: `bt-${backtest.id}`,
    label: backtest.title?.trim() || `Simulation ${backtest.id.slice(0, 8)}`,
    backtest_id: backtest.id,
  };
}

export function overlapKeyForReportPick(pick: ReportPick): string {
  return `pick:${pick.id}`;
}

export function overlapKeyForBacktest(backtestId: string): string {
  return `bt:${backtestId}`;
}

export function overlapKeyForCombinedBundle(bundleId: string): string {
  return `bundle:${bundleId}`;
}

export function selectionItemFromReportPick(
  pick: ReportPick,
  opts?: { linkedBacktestId?: string }
): OverlapSelectionItem {
  return {
    key: overlapKeyForReportPick(pick),
    label: pick.label,
    sourceKind: opts?.linkedBacktestId ? "saved_backtest" : "report_pick",
    reportPickId: pick.id,
    ...(opts?.linkedBacktestId ? { backtestId: opts.linkedBacktestId } : {}),
    ...(isExitPlacementPick(pick) && !opts?.linkedBacktestId && !sliceIdFromExitPlacementPick(pick)
      ? { warning: "Exit placement pick is missing a slice id." }
      : {}),
  };
}

export function selectionItemFromBacktest(backtest: Backtest): OverlapSelectionItem {
  return {
    key: overlapKeyForBacktest(backtest.id),
    label: backtest.title?.trim() || `Simulation ${backtest.id.slice(0, 8)}`,
    sourceKind: "saved_backtest",
    backtestId: backtest.id,
  };
}

/** Combined Kanban card as one overlap participant (saved sim or inline policy tree). */
export function selectionItemFromCombinedBundle(opts: {
  bundleId: string;
  label: string;
  backtestId?: string;
  policyTree?: TradePolicyPolicyPayload | Record<string, unknown>;
  warning?: string;
}): OverlapSelectionItem {
  const backtestId = opts.backtestId?.trim() || undefined;
  if (backtestId) {
    return {
      key: overlapKeyForBacktest(backtestId),
      label: opts.label,
      sourceKind: "saved_backtest",
      backtestId,
      bundleId: opts.bundleId,
      ...(opts.warning ? { warning: opts.warning } : {}),
    };
  }
  return {
    key: overlapKeyForCombinedBundle(opts.bundleId),
    label: opts.label,
    sourceKind: "combined_bundle",
    bundleId: opts.bundleId,
    ...(opts.policyTree ? { policyTree: opts.policyTree } : {}),
    ...(opts.warning ? { warning: opts.warning } : {}),
  };
}

/** Map report picks to saved backtest ids from comparison runs (label / tab hint match). */
export function buildPickToBacktestIdMap(
  picks: ReportPick[],
  comparisonRuns: Array<{
    backtestId?: string;
    label?: string;
    savedTitle?: string;
    pickTabHint?: string;
  }>
): Map<string, string> {
  const map = new Map<string, string>();
  for (const run of comparisonRuns) {
    const btId = run.backtestId?.trim();
    if (!btId) continue;
    const hints = [
      run.pickTabHint?.trim().toLowerCase(),
      run.savedTitle?.trim().toLowerCase(),
      run.label?.trim().toLowerCase(),
    ].filter(Boolean) as string[];

    for (const pick of picks) {
      if (map.has(pick.id)) continue;
      const pl = pick.label.trim().toLowerCase();
      if (!pl) continue;
      const matched = hints.some(
        (h) => h === pl || h.includes(pl) || pl.includes(h)
      );
      if (matched) map.set(pick.id, btId);
    }
  }
  return map;
}

export type BuildOverlapRequestResult = {
  requestPicks: PolicyPickOverlapPick[];
  skipped: Array<{ item: OverlapSelectionItem; reason: string }>;
};

export function buildOverlapRequestFromSelection(
  items: OverlapSelectionItem[],
  opts: {
    reportPicksById: Map<string, ReportPick>;
    backtestsById: Map<string, Backtest>;
    targetAction: PolicyPickOverlapAction;
    catalogCtx: PickImportCatalogContext;
    pickToBacktestId?: Map<string, string>;
    previewCombinations?: string[][];
    /** Per report-pick action overrides (fallback: targetAction). */
    actionByPickId?: Record<string, PolicyPickOverlapAction>;
  }
): BuildOverlapRequestResult {
  const requestPicks: PolicyPickOverlapPick[] = [];
  const skipped: Array<{ item: OverlapSelectionItem; reason: string }> = [];

  for (const item of items.slice(0, MAX_POLICY_PICK_OVERLAP)) {
    if (item.sourceKind === "saved_backtest" && item.backtestId) {
      const bt = opts.backtestsById.get(item.backtestId);
      if (!bt) {
        skipped.push({ item, reason: "Saved simulation not found — refresh the list." });
        continue;
      }
      requestPicks.push({
        ...mapBacktestToOverlapPick(bt),
        // Prefer the selection chip label (e.g. resolved combined policy names).
        ...(item.label.trim() ? { label: item.label.trim() } : {}),
      });
      continue;
    }

    if (item.sourceKind === "combined_bundle") {
      if (item.policyTree) {
        const action =
          (item.bundleId ? opts.actionByPickId?.[item.bundleId] : undefined) ?? opts.targetAction;
        requestPicks.push({
          pick_id: item.bundleId ?? item.key,
          label: item.label,
          policy_tree: item.policyTree,
          action,
        });
        continue;
      }
      skipped.push({
        item,
        reason: "Combined pick has no policy tree or saved simulation to compare.",
      });
      continue;
    }

    if (item.sourceKind === "report_pick" && item.reportPickId) {
      const pick = opts.reportPicksById.get(item.reportPickId);
      if (!pick) {
        skipped.push({ item, reason: "Pick not found in pick list." });
        continue;
      }

      const linkedBacktestId =
        item.backtestId?.trim() ?? opts.pickToBacktestId?.get(pick.id)?.trim();

      const action = opts.actionByPickId?.[pick.id] ?? opts.targetAction;
      const mapped = mapReportPickToOverlapPick(pick, action, opts.catalogCtx, {
        backtestId: linkedBacktestId,
      });
      if (!mapped.ok) {
        skipped.push({ item, reason: mapped.reason });
        continue;
      }
      requestPicks.push(mapped.pick);
    }
  }

  return {
    requestPicks,
    skipped,
  };
}

export function overlapPickIdsFromSelection(items: OverlapSelectionItem[]): string[] {
  return items.map((item) => {
    if (item.sourceKind === "saved_backtest" && item.backtestId) {
      return `bt-${item.backtestId}`;
    }
    if (item.sourceKind === "combined_bundle") {
      return item.bundleId ?? item.key;
    }
    return item.reportPickId ?? item.key;
  });
}

/**
 * Map overlap API pick ids (report / bt- / bundle) back to runnable kanban atomic ids:
 * leaf report-pick ids and/or combined-bundle card ids.
 */
export function resolveRunnableAtomicIdsFromOverlapPickIds(
  pickIds: string[],
  opts: {
    selection: OverlapSelectionItem[];
    reportPickIds: Set<string>;
    bundleIds: Set<string>;
  }
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (id: string | undefined) => {
    const trimmed = id?.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    out.push(trimmed);
  };

  for (const raw of pickIds) {
    const id = raw?.trim();
    if (!id) continue;

    if (id.startsWith("bt-")) {
      const btId = id.slice(3);
      const match = opts.selection.find(
        (s) => s.backtestId === btId || s.backtestId === id || `bt-${s.backtestId}` === id
      );
      if (match?.reportPickId && opts.reportPickIds.has(match.reportPickId)) {
        push(match.reportPickId);
        continue;
      }
      if (match?.bundleId && opts.bundleIds.has(match.bundleId)) {
        push(match.bundleId);
        continue;
      }
      continue;
    }

    if (opts.reportPickIds.has(id)) {
      push(id);
      continue;
    }
    if (opts.bundleIds.has(id)) {
      push(id);
      continue;
    }

    const byBundle = opts.selection.find((s) => s.bundleId === id);
    if (byBundle?.bundleId && opts.bundleIds.has(byBundle.bundleId)) {
      push(byBundle.bundleId);
      continue;
    }
    const byReport = opts.selection.find((s) => s.reportPickId === id);
    if (byReport?.reportPickId && opts.reportPickIds.has(byReport.reportPickId)) {
      push(byReport.reportPickId);
    }
  }

  return out;
}

export function resolveReportPickIdsFromOverlapPickIds(
  pickIds: string[],
  items: OverlapSelectionItem[]
): string[] {
  const out: string[] = [];
  for (const id of pickIds) {
    const mapped = items.find((item) => {
      if (item.sourceKind === "report_pick" && item.reportPickId === id) return true;
      if (item.sourceKind === "saved_backtest" && `bt-${item.backtestId}` === id) return false;
      return item.reportPickId === id;
    });
    if (mapped?.reportPickId) out.push(mapped.reportPickId);
    else if (!id.startsWith("bt-")) out.push(id);
  }
  return out.filter(Boolean);
}

export function isExitPlacementBacktest(backtest: Backtest): boolean {
  const scope = backtest.scope as Record<string, unknown> | undefined;
  if (!scope) return false;
  return (
    Array.isArray(scope.simulate_legs) ||
    Array.isArray(scope.simulated_legs) ||
    backtest.policy_schema_version === "policy_tree.v2"
  );
}

export function catalogCtxFromFields(
  fieldsByPath: Map<string, import("../types/tradePolicy").TradePolicyCatalogField>,
  marginalEqPrefixes?: Record<string, string>
): PickImportCatalogContext {
  return { fieldsByPath, marginalEqPrefixes };
}

export function policyNodeCountGuard(nodes: unknown[]): boolean {
  return nodes.every((n) => isPolicyNode(n));
}
